
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { API_ENDPOINT, getAuthHeader } from "@/config/environment";
import { Bill } from "../../types";
import { useSearchParams } from "react-router-dom";

export function useBillsForSchool(
  setLoading: (loading: boolean) => void,
  setBills: (bills: Bill[]) => void,
  setFetchComplete: (complete: boolean) => void,
  fetchComplete: boolean
) {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const fetchBillsForSchool = useCallback(async (schoolId: string) => {
    // Skip if we've already completed a fetch
    if (fetchComplete) {
      console.log('Bills fetch already complete, skipping...');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching bills for school ID:', schoolId);
      
      // Use GET request method with URL parameters
      let url = `${API_ENDPOINT}/get-school-subscriptions?schoolId=${encodeURIComponent(schoolId)}`;
      if (token) {
        url += `&token=${encodeURIComponent(token)}`;
      }
      
      const response = await fetch(
        url,
        {
          method: 'GET',
          headers: getAuthHeader()
        }
      );

      if (!response.ok) {
        let errorMessage = 'Failed to fetch bills';
        try {
          const errorData = await response.json();
          console.error('Error response from API:', errorData);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      
      if (!responseData.success) {
        console.error('Unsuccessful response:', responseData);
        throw new Error(responseData.error || 'Failed to fetch bills');
      }
      
      // Extract all bills from all subscriptions
      const allBills: Bill[] = [];
      
      if (responseData.data.subscriptions) {
        responseData.data.subscriptions.forEach((subscription: any) => {
          if (subscription.bills && subscription.bills.length > 0) {
            subscription.bills.forEach((bill: any) => {
              // Add plan information from the subscription
              const enhancedBill = {
                ...bill,
                subscription_id: bill.subscription_id || subscription.id, // Ensure subscription_id is set
                discount_percentage: bill.discount_type === 'percentage' ? 
                  parseFloat(bill.discount.toString()) : null,
                bill_component_id: bill.bill_component_id,
                plan: {
                  name: subscription.plan?.name || 'Unknown Plan',
                  price: subscription.plan?.price || 0,
                  product: {
                    name: subscription.plan?.product?.name || 'Unknown Product'
                  }
                }
              } as Bill;
              
              allBills.push(enhancedBill);
            });
          }
        });
      }
      
      // Directly use bills from the response if available
      if (responseData.data.bills && responseData.data.bills.length > 0) {
        for (const bill of responseData.data.bills) {
          // Only add bills that aren't already included from subscriptions
          const exists = allBills.some(b => b.id === bill.id);
          if (!exists) {
            // Find the corresponding subscription to get product info
            const subscription = responseData.data.subscriptions?.find(
              (sub: any) => sub.id === bill.subscription_id
            );
            
            // Make sure plan data is properly structured
            const enhancedBill = {
              ...bill,
              discount_percentage: bill.discount_type === 'percentage' ? 
                parseFloat(bill.discount.toString()) : null,
              bill_component_id: bill.bill_component_id,
              plan: {
                name: bill.plan_id ? (subscription?.plan?.name || 'Unknown Plan') : 'Unknown Plan',
                price: bill.plan_price || subscription?.plan?.price || 0,
                product: {
                  name: subscription?.plan?.product?.name || 'Unknown Product'
                }
              }
            } as Bill;
            
            allBills.push(enhancedBill);
          }
        }
      }
      
      console.log('Processed bills:', allBills);
      setBills(allBills);
      setFetchComplete(true);
    } catch (error) {
      console.error('Error fetching bills for school:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fetch bills",
      });
      setBills([]);
      setFetchComplete(true);
    } finally {
      setLoading(false);
    }
  }, [fetchComplete, token, toast, setBills, setLoading, setFetchComplete]);

  return { fetchBillsForSchool };
}
